// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/Workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface PostCreatePersonalWorkspaceProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  body?: any;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Creates a new personal workspace
 * @param getAccessToken Function to acquire access token
 * @param body Optional request body
 * @returns Promise with API response or error
 */
export const postCreatePersonalWorkspace = async ({
  getAccessToken,
  body,
}: PostCreatePersonalWorkspaceProps): Promise<any> => {
  try {
    console.log('🔄 Creating personal workspace...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/create/personal`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: createHeaders(token),
      body: body ? JSON.stringify(body) : undefined,
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Personal workspace created successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error creating personal workspace:', error);
    throw error;
  }
};

